import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, Building2, MapPin, ArrowRight } from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    customer_type: "residential",
    company_name: "",
    address: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.customer_type === "commercial" && !formData.company_name) {
      toast.error("Company name is required for commercial accounts");
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        customer_type: formData.customer_type,
        company_name: formData.company_name || null,
        address: formData.address || null,
      });
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-lg mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img 
              src="/protocall-logo.png" 
              alt="ProToCall" 
              className="h-16 w-auto"
            />
          </Link>
          <h1
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Create Your Account
          </h1>
          <p className="text-slate-600 mt-2">Join ProToCall for easy booking and payments</p>
        </div>

        <Card className="bg-white border border-slate-200 shadow-lg">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Account Type */}
              <div>
                <Label className="text-slate-700 mb-3 block">Account Type *</Label>
                <RadioGroup
                  value={formData.customer_type}
                  onValueChange={(value) => setFormData({ ...formData, customer_type: value })}
                  className="flex gap-4"
                >
                  <div
                    className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.customer_type === "residential"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setFormData({ ...formData, customer_type: "residential" })}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="residential" id="residential" />
                      <Label htmlFor="residential" className="cursor-pointer font-medium">
                        Residential
                      </Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">Home services</p>
                  </div>
                  <div
                    className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.customer_type === "commercial"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setFormData({ ...formData, customer_type: "commercial" })}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="commercial" id="commercial" />
                      <Label htmlFor="commercial" className="cursor-pointer font-medium">
                        Commercial
                      </Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">Business services</p>
                  </div>
                </RadioGroup>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-slate-700">
                  Full Name *
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="pl-10 h-11"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              {/* Company Name - Only for Commercial */}
              {formData.customer_type === "commercial" && (
                <div>
                  <Label htmlFor="company_name" className="text-slate-700">
                    Company Name *
                  </Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Your Company LLC"
                      className="pl-10 h-11"
                      data-testid="register-company-input"
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-slate-700">
                  Email Address *
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="pl-10 h-11"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-slate-700">
                  Phone Number *
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="pl-10 h-11"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="address" className="text-slate-700">
                  Address (Optional)
                </Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, State"
                    className="pl-10 h-11"
                    data-testid="register-address-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-slate-700">
                    Password *
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700">
                    Confirm Password *
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11"
                data-testid="register-submit-btn"
              >
                {loading ? "Creating Account..." : "Create Account"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-slate-400">or</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
              data-testid="google-register-btn"
            >
              <svg className="mr-2 w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <div className="mt-6 text-center text-sm">
              <span className="text-slate-600">Already have an account? </span>
              <Link
                to="/login"
                className="text-orange-500 hover:text-orange-600 font-medium"
                data-testid="login-link"
              >
                Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
